import { create } from 'zustand'

/**
 * Staging-only override for the SOCIAL FEED's display currency, set by
 * SocialCurrencyDebugPanel. It previews the feed ONLY — chips/balance/limits
 * stay on the launch currency by design (overriding `config.bet.currency`
 * wholesale would break betting).
 *
 * Gating (both belts, matching the InventoryDebugPanel precedent):
 *   - the panel only mounts behind `NEXT_PUBLIC_ENV !== 'production'`
 *     (layout.tsx), and
 *   - useSocialCurrencyOverride hard-returns `null` when
 *     `NEXT_PUBLIC_ENV === 'production'`, so even an accidentally-mounted
 *     panel is inert in prod.
 *
 * The store defaults to `null` = zero behaviour change when unused.
 */
export interface SocialCurrencyOverride {
  /** Currency code to preview the feed in (e.g. 'ZAR', 'GLD', 'BTC'). */
  currency: string
  /**
   * Fixed test rate, units per 1 USD — same direction as GameConfig.usdRate.
   * Deliberately optional: when an override is ACTIVE, this value REPLACES
   * the server rate even when undefined (read sites use
   * `override ? override.usdRate : usdRate`, NOT `??`), so the BTC preset
   * demonstrates the excluded behaviour (`$` + USD values) instead of
   * borrowing a live rate.
   */
  usdRate?: number
}

interface SocialCurrencyOverrideStore {
  override: SocialCurrencyOverride | null
  setOverride: (override: SocialCurrencyOverride | null) => void
}

/** Module-level store: the panel writes, the three social read sites read. */
export const useSocialCurrencyOverrideStore = create<SocialCurrencyOverrideStore>()((set) => ({
  override: null,
  setOverride: (override) => {
    set({ override })
  }
}))

/**
 * The override the social read sites consume — or `null` unconditionally in
 * production, no matter what the store holds. That hard null IS the
 * "staging only" guarantee (the panel mount gate is only the first belt).
 */
export const useSocialCurrencyOverride = (): SocialCurrencyOverride | null => {
  // Subscribe BEFORE the env gate — hooks must run unconditionally.
  const override = useSocialCurrencyOverrideStore((state) => state.override)
  if (process.env.NEXT_PUBLIC_ENV === 'production') {
    return null
  }
  return override
}
